import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import conorPhoto from './assets/founders/conor.jpeg';
import jimPhoto from './assets/founders/jim.jpeg';
import Brand from './components/Brand';
import ThemeToggle from './components/ThemeToggle';

const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT ||
  'https://contact-iqe3qru7fa-uc.a.run.app';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  role: '',
  projectType: '',
  budget: '',
  message: '',
  newsletter: false,
  website: '', // honeypot - must stay empty
};

type ContactForm = typeof EMPTY_FORM;
type SubmitStatus = 'idle' | 'sending' | 'success' | 'error';

function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [form, setForm] = useState<ContactForm>(EMPTY_FORM);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }
      setStatus('success');
      setForm(EMPTY_FORM);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-surface-2/90 backdrop-blur-sm border-b border-line z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[72px]">
            <Brand />

            {/* Right: nav links + theme toggle + mobile menu */}
            <div className="flex items-center gap-4">
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              <a href="#home" className="text-content-2 hover:text-accent font-medium transition">Overview</a>
              <a href="#solutions" className="text-content-2 hover:text-accent font-medium transition">Solutions</a>
              <a href="#industries" className="text-content-2 hover:text-accent font-medium transition">Industries</a>
              <Link to="/case-studies" className="text-content-2 hover:text-accent font-medium transition">Case Studies</Link>
              <a href="#about" className="text-content-2 hover:text-accent font-medium transition">About</a>
              <a href="#contact" className="text-content-2 hover:text-accent font-medium transition">Contact</a>
            </div>
            
            <ThemeToggle />

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-content-2 hover:text-content focus:outline-none"
              aria-label="Toggle navigation menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <a
                  href="#home"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-content-2 hover:text-accent hover:bg-surface-3 rounded-md font-medium transition"
                >
                  Overview
                </a>
                <a
                  href="#solutions"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-content-2 hover:text-accent hover:bg-surface-3 rounded-md font-medium transition"
                >
                  Solutions
                </a>
                <a
                  href="#industries"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-content-2 hover:text-accent hover:bg-surface-3 rounded-md font-medium transition"
                >
                  Industries
                </a>
                <a
                  href="/case-studies"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-content-2 hover:text-accent hover:bg-surface-3 rounded-md font-medium transition"
                >
                  Case Studies
                </a>
                <a
                  href="#about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-content-2 hover:text-accent hover:bg-surface-3 rounded-md font-medium transition"
                >
                  About
                </a>
                <a
                  href="#contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-content-2 hover:text-accent hover:bg-surface-3 rounded-md font-medium transition"
                >
                  Contact
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-20 min-h-screen flex items-center bg-gradient-to-br from-surface-2 to-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="flex justify-center mb-10">
              <Brand className="h-24 md:h-32" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-content mb-6 leading-tight">
              Engineering Excellence at
              <span className="block text-accent">Enterprise Scale</span>
            </h1>
            <p className="text-xl md:text-2xl text-content-2 mb-8 max-w-3xl mx-auto font-light">
              From startup MVPs to Fortune 500 digital transformations, we architect and ship
              production software, and the applied AI systems that set it apart.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="px-8 py-3 bg-accent text-surface font-semibold rounded-lg hover:bg-accent-hover transition shadow-lg">
                Let's Talk Tech
              </a>
              <a href="#solutions" className="px-8 py-3 bg-transparent text-accent font-semibold rounded-lg border-2 border-accent hover:bg-accent hover:text-surface transition">
                Explore Solutions
              </a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div>
                <div className="text-3xl font-bold text-accent">Thousands</div>
                <div className="text-content-2">AI Agents Created</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent">200+</div>
                <div className="text-content-2">Projects Delivered</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent">Fortune 500</div>
                <div className="text-content-2">Trusted By</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions & Services Section */}
      <section id="solutions" className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-content mb-4">Solutions & Services</h2>
            <p className="text-xl text-content-2 max-w-3xl mx-auto">
              Comprehensive technical capabilities across the entire software development lifecycle
            </p>
          </div>
          
          {/* Applied AI - flagship */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-content mb-3">Applied AI</h3>
            <p className="text-content-2 mb-8 max-w-3xl leading-relaxed">
              We build production AI that's governed, auditable, and cost-controlled. Not demos.
              From autonomous agents to LLM evaluation pipelines, we ship systems that hold up under
              real workloads, including our own product,{' '}
              <a href="https://pipebot.io" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent-hover font-medium underline underline-offset-2">Pipebot</a>.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Agentic Systems</h4>
                <p className="text-content-2 mb-4">Autonomous, multi-step agents that use tools and take real actions reliably.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Agents</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Tool Use</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Orchestration</span>
                </div>
              </div>

              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">LLM Evaluation &amp; Guardrails</h4>
                <p className="text-content-2 mb-4">LLM-as-judge scoring, evidence-backed outputs with citations, and reliable guardrails.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">LLM-as-Judge</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Evals</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Guardrails</span>
                </div>
              </div>

              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">RAG &amp; Retrieval</h4>
                <p className="text-content-2 mb-4">Retrieval-augmented generation over large, unstructured document sets.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">RAG</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Vector DBs</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Embeddings</span>
                </div>
              </div>

              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Model Integration</h4>
                <p className="text-content-2 mb-4">Multi-provider LLM integration across OpenAI, Anthropic, and Google.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">OpenAI</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Anthropic</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Gemini</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Vertex AI</span>
                </div>
              </div>

              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Governance &amp; Auditability</h4>
                <p className="text-content-2 mb-4">Full audit trails, decision lineage, and observability so every AI output is traceable.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Audit Trails</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Lineage</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Observability</span>
                </div>
              </div>

              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Cost &amp; Performance</h4>
                <p className="text-content-2 mb-4">Token, latency, and throughput optimization that keeps AI economical at scale.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Cost Optimization</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Caching</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Latency</span>
                </div>
              </div>
            </div>
          </div>

          {/* Cloud Infrastructure */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-content mb-6">Cloud Infrastructure & DevOps</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Cloud Platforms</h4>
                <p className="text-content-2 mb-3">Expert implementation across AWS, Azure, and Google Cloud Platform</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">AWS</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Azure</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">GCP</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Infrastructure as Code</h4>
                <p className="text-content-2 mb-3">Automated, scalable infrastructure deployment and management</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Terraform</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">CloudFormation</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">GitLab CI/CD</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Container Orchestration</h4>
                <p className="text-content-2 mb-3">Modern containerized application deployment and scaling</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Kubernetes</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Docker</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">ECS/EKS</span>
                </div>
              </div>
            </div>
          </div>

          {/* Data & Analytics */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-content mb-6">Data Integration & Analytics</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Data Engineering</h4>
                <p className="text-content-2 mb-3">End-to-end data pipeline design and implementation</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">dbt</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Airflow</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">METL</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Kafka</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Data Warehousing</h4>
                <p className="text-content-2 mb-3">Modern cloud data warehouse architecture and optimization</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Snowflake</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Teradata</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Netezza</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">BigQuery</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Business Intelligence</h4>
                <p className="text-content-2 mb-3">Actionable insights through advanced visualization and analytics</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">MicroStrategy</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Tableau</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Power BI</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Custom BI</span>
                </div>
              </div>
            </div>
          </div>

          {/* Supply Chain & Inventory */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-content mb-6">Supply Chain & Inventory Management</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Inventory Optimization</h4>
                <p className="text-content-2 mb-3">ML-driven allocation and replenishment systems</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Forecasting</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Allocation Logic</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Demand Planning</h4>
                <p className="text-content-2 mb-3">Advanced predictive analytics for demand forecasting</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">ML Forecasting</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Planning Tools</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">POS Integration</h4>
                <p className="text-content-2 mb-3">Modern point-of-sale systems and retail analytics</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Real-time Sync</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Analytics</span>
                </div>
              </div>
            </div>
          </div>

          {/* Specialized Solutions */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-content mb-6">Specialized Solutions</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">POS System Implementation</h4>
                <p className="text-content-2 mb-3">Next-generation point-of-sale rollouts and integrations</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Jumpmind</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Real-time Sync</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Legacy Modernization</h4>
                <p className="text-content-2 mb-3">Transform legacy systems into modern cloud-native solutions</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Oracle Migration</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Strangler Pattern</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Price Management Systems</h4>
                <p className="text-content-2 mb-3">Multi-currency pricing with conflict detection</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Global Pricing</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">10x Performance</span>
                </div>
              </div>
            </div>
          </div>

          {/* Development Capabilities */}
          <div>
            <h3 className="text-2xl font-bold text-content mb-6">Full-Stack Development</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Frontend Excellence</h4>
                <p className="text-content-2 mb-3">Modern, responsive user interfaces with optimal performance</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">React</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Angular</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">React Native</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">TypeScript</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Backend Architecture</h4>
                <p className="text-content-2 mb-3">Scalable API design and microservices implementation</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Django</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">.NET Core</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Python</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Node.js</span>
                </div>
              </div>
              
              <div className="bg-surface-3 p-6 rounded-xl border border-line">
                <h4 className="font-semibold text-lg mb-3 text-content">Database Design</h4>
                <p className="text-content-2 mb-3">Optimized data storage solutions for any scale</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">PostgreSQL</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">SQL Server</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">MongoDB</span>
                  <span className="px-3 py-1 bg-accent/10 text-accent border border-accent/40 rounded-full text-sm font-medium">Redis</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 bg-surface-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-content mb-4">Industries We Serve</h2>
            <p className="text-xl text-content-2 max-w-3xl mx-auto">
              Deep domain expertise across multiple verticals with proven track records
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-surface-3 p-8 rounded-xl border border-line hover:shadow-xl transition">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-content">Retail & E-commerce</h3>
              <p className="text-content-2 mb-4">Omnichannel solutions, inventory management, and customer analytics for modern retail</p>
              <ul className="text-sm text-content-2 space-y-1">
                <li>• Multi-channel inventory sync</li>
                <li>• Customer personalization engines</li>
                <li>• Real-time analytics dashboards</li>
              </ul>
            </div>


            <div className="bg-surface-3 p-8 rounded-xl border border-line hover:shadow-xl transition">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-content">Gaming</h3>
              <p className="text-content-2 mb-4">High-performance backend systems and player analytics for gaming platforms</p>
              <ul className="text-sm text-content-2 space-y-1">
                <li>• Real-time multiplayer infrastructure</li>
                <li>• Player behavior analytics</li>
                <li>• Monetization optimization</li>
              </ul>
            </div>


            <div className="bg-surface-3 p-8 rounded-xl border border-line hover:shadow-xl transition">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-content">Enterprise Solutions</h3>
              <p className="text-content-2 mb-4">Large-scale digital transformation for Fortune 500 companies</p>
              <ul className="text-sm text-content-2 space-y-1">
                <li>• Legacy system modernization</li>
                <li>• Enterprise integration</li>
                <li>• Global deployment strategies</li>
              </ul>
            </div>

            <div className="bg-surface-3 p-8 rounded-xl border border-line hover:shadow-xl transition">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-content">Manufacturing</h3>
              <p className="text-content-2 mb-4">Industrial automation and supply chain optimization</p>
              <ul className="text-sm text-content-2 space-y-1">
                <li>• Production optimization</li>
                <li>• Quality control systems</li>
                <li>• Supply chain visibility</li>
              </ul>
            </div>

            <div className="bg-surface-3 p-8 rounded-xl border border-line hover:shadow-xl transition">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-content">Entertainment & Hospitality</h3>
              <p className="text-content-2 mb-4">Guest experience platforms and venue management systems</p>
              <ul className="text-sm text-content-2 space-y-1">
                <li>• Guest journey orchestration</li>
                <li>• CRM & loyalty programs</li>
                <li>• Multi-venue integration</li>
              </ul>
            </div>

            <div className="bg-surface-3 p-8 rounded-xl border border-line hover:shadow-xl transition">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-content">Blockchain & Web3</h3>
              <p className="text-content-2 mb-4">Enterprise blockchain integration and distributed systems</p>
              <ul className="text-sm text-content-2 space-y-1">
                <li>• Smart contract development</li>
                <li>• Enterprise DLT integration</li>
                <li>• Tokenization platforms</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-content mb-4">Featured Case Studies</h2>
            <p className="text-xl text-content-2 max-w-3xl mx-auto">
              Real-world examples of transformative solutions we've delivered
            </p>
          </div>

          <div className="space-y-12">
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-accent font-semibold">AI Governance Platform</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Governed AI for Audit &amp; Controls</h3>
                  <p className="text-content-2 mb-6">
                    We engineered an AI platform that runs complex audit and controls procedures end to
                    end. Multi-step orchestration plans each engagement, executes governed analytical
                    procedures, and applies LLM-as-judge evaluation to assess findings against defined
                    assertions. Every conclusion is backed by linked evidence and a complete, replayable
                    audit trail.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Orchestrated, multi-step governed procedures</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">LLM-as-judge assertion evaluation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Evidence-linked findings with full traceability</span>
                    </div>
                  </div>
                </div>
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Multi-Step</div>
                      <div className="text-content-2">Orchestration</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">LLM-as-Judge</div>
                      <div className="text-content-2">Evaluation</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">End-to-End</div>
                      <div className="text-content-2">Audit Trails</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Evidence</div>
                      <div className="text-content-2">Backed Findings</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1 bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">100+</div>
                      <div className="text-content-2">Stores in 4 Months</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">100x</div>
                      <div className="text-content-2">Growth Rate</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Real-time</div>
                      <div className="text-content-2">Inventory Sync</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Full</div>
                      <div className="text-content-2">3PL Integration</div>
                    </div>
                  </div>
                </div>
                <div className="order-1 md:order-2">
                  <span className="text-accent font-semibold">Cannabis Startup</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Rapid Retail Expansion Platform</h3>
                  <p className="text-content-2 mb-6">
                    We enabled a CBD retailer to achieve explosive growth from 1 to 100+ stores in just 4 months. 
                    We built a centralized command center for POS data, inventory management, and automated 
                    replenishment. We created a custom PIM system with seamless Shopify integration, enabling 
                    real-time product updates, stock synchronization, and order fulfillment tracking.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Centralized POS & inventory hub</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Custom PIM with Shopify integration</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Automated 3PL shipping & tracking</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-accent font-semibold">AI Startup</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">AI Document-Intelligence Platform</h3>
                  <p className="text-content-2 mb-6">
                    We partnered with an early-stage team to build the AI engine behind their
                    document-intelligence platform. We engineered the pipeline that ingests massive
                    volumes of unstructured documents, runs multi-stage LLM analysis with automated
                    quality scoring, and returns evidence-backed findings with citations and complete
                    audit trails, reliably and at scale.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Multi-format document processing</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Evidence-based finding generation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">LLM-as-judge quality scoring</span>
                    </div>
                  </div>
                </div>
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">1000s</div>
                      <div className="text-content-2">Documents Analyzed</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Multiple</div>
                      <div className="text-content-2">Data Sources</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Evidence</div>
                      <div className="text-content-2">Based Findings</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Full</div>
                      <div className="text-content-2">Audit Trails</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* View More Case Studies */}
          <div className="mt-16 text-center">
            <Link to="/case-studies#top" className="inline-block px-8 py-3 bg-transparent text-accent font-semibold rounded-lg border-2 border-accent hover:bg-accent hover:text-surface transition">
              View All Case Studies →
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-surface-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-content mb-4">About Databurst Technologies</h2>
            <p className="text-xl text-content-2 max-w-3xl mx-auto">
              We are architects of digital transformation, bridging the gap between 
              cutting-edge technology and business innovation
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-content">Our Mission</h3>
              <p className="text-content-2 mb-6">
                To empower businesses of all sizes with enterprise-grade technology solutions 
                that drive growth, efficiency, and competitive advantage. We believe that 
                exceptional software engineering should be accessible to startups and Fortune 
                500 companies alike.
              </p>
              <p className="text-content-2">
                Our team combines deep technical expertise with business acumen to deliver 
                solutions that not only solve immediate challenges but also scale with your 
                organization's growth trajectory.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-content">Our Approach</h3>
              <p className="text-content-2 mb-6">
                We practice what we call "Pragmatic Innovation" - leveraging proven technologies 
                and methodologies while staying at the forefront of emerging trends. This ensures 
                our solutions are both reliable and future-proof.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-accent mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-content-2">Agile methodology with continuous delivery</span>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-accent mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-content-2">Security-first architecture design</span>
                </div>
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-accent mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-content-2">Transparent communication and reporting</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-surface-3 rounded-2xl p-12 border border-line">
            <h3 className="text-2xl font-bold mb-8 text-center text-content">Leadership Team</h3>
            <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
              <div className="text-center">
                <img
                  src={conorPhoto}
                  alt="Conor Seabrook"
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover object-top"
                />
                <h4 className="font-semibold text-xl text-content mb-2">Conor Seabrook</h4>
                <p className="text-accent mb-4">Solution Architect & Founder</p>
                <div className="space-y-1 text-sm text-content-2">
                  <p>AI engineer: agentic systems, LLM orchestration, and evaluation</p>
                  <p>Former CTO managing 20+ international engineers</p>
                  <p>Founded and operates successful SaaS company (since 2005)</p>
                  <p>Built and scaled mobile game studio with large user base</p>
                  <p>Expert in high-availability systems and DevOps automation</p>
                  <p>Full-stack development across enterprise and startup environments</p>
                </div>
              </div>
              <div className="text-center">
                <img
                  src={jimPhoto}
                  alt="Jim Loeffler"
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover object-top"
                />
                <h4 className="font-semibold text-xl text-content mb-2">Jim Loeffler</h4>
                <p className="text-accent mb-4">Solution Architect & Founder</p>
                <div className="space-y-1 text-sm text-content-2">
                  <p>Applied AI for business: automation, analytics, and decision support</p>
                  <p>Deep expertise in Business Intelligence and Data Warehousing</p>
                  <p>Specialized in retail, manufacturing, and gaming industries</p>
                  <p>Expert in performance optimization (10+ hour to real-time)</p>
                  <p>Extensive experience with Fortune 500 implementations</p>
                  <p>Founded and operates successful SaaS company</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Databurst Section */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-content mb-4">Why Choose Databurst?</h2>
            <p className="text-xl text-content-2 max-w-3xl mx-auto">
              What sets us apart in the enterprise consulting landscape
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-content mb-2">Founders Who Code</h3>
              <p className="text-content-2 text-sm">
                Both founders are hands-on architects who actively code, not just consult
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-content mb-2">Enterprise to Startup</h3>
              <p className="text-content-2 text-sm">
                Proven success from Fortune 500 implementations to rapid startup scaling
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-content mb-2">10x Performance</h3>
              <p className="text-content-2 text-sm">
                Track record of achieving 10x improvements in system performance
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-surface" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-content mb-2">Deep Domain Expertise</h3>
              <p className="text-content-2 text-sm">
                Specialized knowledge in retail, e-commerce, and enterprise systems
              </p>
            </div>
          </div>

          <div className="mt-16 bg-surface-3 rounded-2xl p-8 border border-line">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-accent mb-2">Thousands</div>
                <p className="text-content-2">AI Agents Created</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-accent mb-2">200+</div>
                <p className="text-content-2">Successful Implementations</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-accent mb-2">20+</div>
                <p className="text-content-2">Years as Business Owners</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-surface to-surface-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-content mb-4">Ready to Revolutionize Your Tech?</h2>
            <p className="text-xl text-content-2 max-w-3xl mx-auto">
              Ready to transform your business with enterprise-grade technology solutions? 
              Our team is here to help you navigate your digital transformation journey.
            </p>
          </div>

          <div className="max-w-2xl mx-auto bg-surface-2 rounded-2xl p-8 border border-line">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Honeypot - hidden from users, traps bots */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-content-2 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-content-2 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-content-2 mb-2">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-content-2 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-content-2 mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-content-2 mb-2">
                    Your Role *
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                    required
                  >
                    <option value="">Select your role</option>
                    <option>C-Suite Executive</option>
                    <option>IT Director</option>
                    <option>Engineering Manager</option>
                    <option>Product Manager</option>
                    <option>Founder/Owner</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="project-type" className="block text-sm font-medium text-content-2 mb-2">
                  Project Type *
                </label>
                <select
                  id="project-type"
                  name="projectType"
                  value={form.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                  required
                >
                  <option value="">Select project type</option>
                  <option>Greenfield Development</option>
                  <option>Legacy Modernization</option>
                  <option>Cloud Migration</option>
                  <option>Data & Analytics Platform</option>
                  <option>AI/ML Implementation</option>
                  <option>System Integration</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-content-2 mb-2">
                  Budget Range
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  <option value="">Select budget range</option>
                  <option>$50K - $100K</option>
                  <option>$100K - $250K</option>
                  <option>$250K - $500K</option>
                  <option>$500K - $1M</option>
                  <option>$1M+</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-content-2 mb-2">
                  Tell us about your project *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-line rounded-lg bg-surface text-content placeholder:text-content-3 focus:ring-2 focus:ring-accent focus:border-transparent"
                  placeholder="Please describe your project requirements, timeline, and any specific challenges you're facing..."
                  required
                ></textarea>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="newsletter"
                  name="newsletter"
                  checked={form.newsletter}
                  onChange={handleChange}
                  className="w-4 h-4 text-accent border-line rounded focus:ring-accent"
                />
                <label htmlFor="newsletter" className="text-sm text-content-2">
                  I'd like to receive insights and updates from Databurst Technologies
                </label>
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-accent text-surface font-semibold py-3 rounded-lg hover:bg-accent-hover transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? 'Sending…' : 'Start the Conversation'}
              </button>

              {status === 'success' && (
                <p className="text-center text-green-400 text-sm" role="status">
                  Thanks for reaching out. We've received your message and will be in touch shortly.
                </p>
              )}
              {status === 'error' && (
                <p className="text-center text-red-400 text-sm" role="alert">
                  {errorMsg || 'Something went wrong. Please try again or email us directly.'}
                </p>
              )}

              <p className="text-xs text-content-2 text-center">
                By submitting this form, you agree to our <Link to="/privacy" className="text-accent hover:text-accent-hover">Privacy Policy</Link> and <Link to="/terms" className="text-accent hover:text-accent-hover">Terms of Service</Link>.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-2 text-content py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <Brand className="h-9 mb-4" />
              <p className="text-content-2 mb-4">
                Engineering excellence at enterprise scale.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2 text-content-2">
                <li><a href="#solutions" className="hover:text-accent transition">Cloud Infrastructure</a></li>
                <li><a href="#solutions" className="hover:text-accent transition">Data & Analytics</a></li>
                <li><a href="#solutions" className="hover:text-accent transition">Applied AI</a></li>
                <li><a href="#solutions" className="hover:text-accent transition">Supply Chain</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-content-2">
                <li><a href="#about" className="hover:text-accent transition">About Us</a></li>
                <li><a href="#case-studies" className="hover:text-accent transition">Case Studies</a></li>
                <li><a href="#contact" className="hover:text-accent transition">Contact</a></li>
              </ul>
            </div>

          </div>

          <div className="mt-8 pt-8 border-t border-line flex flex-col md:flex-row justify-between items-center">
            <p className="text-content-2 text-sm">
              © 2017-2026 Databurst Technologies LLC. All rights reserved.
            </p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-content-2 hover:text-accent text-sm transition">Privacy Policy</Link>
              <Link to="/terms" className="text-content-2 hover:text-accent text-sm transition">Terms of Service</Link>
              <Link to="/cookies" className="text-content-2 hover:text-accent text-sm transition">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;